/**
 * Menu Component
 *
 * Displays the coffee shop menu with a grid of menu items.
 * Features:
 * - Grid layout: 1 column on mobile, 2 columns on desktop
 * - 10 menu items displayed
 * - Section header with title and separator
 * - Call-to-action button
 */

import Separator from "../Separator";

import MenuItem from "./MenuItem";

// Menu items data - Coffee offerings with images, names, descriptions, and prices
const menuItems = [
  {
    imgSrc: "/assets/menu/coffee-1.png",
    name: "Espresso",
    description: "Single-origin micro-lot, bright acidity with caramel notes.",
    price: 3.3,
  },
  {
    imgSrc: "/assets/menu/coffee-2.png",
    name: "Cappuccino",
    description: "Classic 6oz ratio with silky microfoam and house espresso.",
    price: 4.5,
  },
  {
    imgSrc: "/assets/menu/coffee-3.png",
    name: "Latte",
    description: "Creamy texture, balanced with our seasonal roast profile.",
    price: 4.2,
  },
  {
    imgSrc: "/assets/menu/coffee-4.png",
    name: "Americano",
    description: "Double espresso marks over structured water for clarity.",
    price: 3.5,
  },
  {
    imgSrc: "/assets/menu/coffee-5.png",
    name: "Mocha",
    description: "70% Single-origin dark cacao meets velvety espresso.",
    price: 5.4,
  },
  {
    imgSrc: "/assets/menu/coffee-1.png",
    name: "Macchiato",
    description: "Espresso shot marked with a touch of aerated milk.",
    price: 3.75,
  },
  {
    imgSrc: "/assets/menu/coffee-2.png",
    name: "Flat White",
    description: "Ristretto double shot with thin, velvety microfoam.",
    price: 4.25,
  },
  {
    imgSrc: "/assets/menu/coffee-3.png",
    name: "Iced Coffee",
    description: "Flash-chilled specialty brew to preserve delicate aromatics.",
    price: 3.75,
  },
  {
    imgSrc: "/assets/menu/coffee-4.png",
    name: "Affogato",
    description: "Double espresso over artisanal Tahitian vanilla cream.",
    price: 5.5,
  },
  {
    imgSrc: "/assets/menu/coffee-5.png",
    name: "Irish Coffee",
    description: "A sophisticated blend of aged whiskey and cold brew.",
    price: 6.5,
  },
] as const; // as const makes the array readonly for TypeScript type inference

const Menu = () => {
  return (
    <section className="pt-12 pb-16 xl:pt-16 xl:pb-36" id="menu">
      <div className="container mx-auto">
        {/* Section Header */}
        <div className="flex flex-col gap-4 mb-12 xl:mb-24">
          <h2 className="h2 text-center">Our Menu</h2>
          <div className="mb-4">
            <Separator bg="accent" />
          </div>
          {/* Section Description */}
          <p className="text-center max-w-155 mx-auto">
            From seasonal single-origins to our house-crafted botanical
            infusions, every item on our menu is a testament to the pursuit of
            coffee excellence.
          </p>
        </div>

        {/* Menu Grid and Button */}
        <div className="flex flex-col items-center gap-12 xl:gap-24">
          {/* Menu Items Grid - Responsive: 1 column mobile, 2 columns desktop */}
          <div className="w-full grid grid-cols-1 xl:grid-cols-2 gap-y-8 gap-x-16 place-content-center">
            {menuItems.map((item, index) => {
              const { name, description, price, imgSrc } = item;

              return (
                <MenuItem
                  key={index}
                  description={description}
                  imgSrc={imgSrc}
                  name={name}
                  price={price}
                />
              );
            })}
          </div>
          {/* Call-to-Action Button */}
          <button aria-label="Order now on WhatsApp" className="btn">
            Order now
          </button>
        </div>
      </div>
    </section>
  );
};

export default Menu;
